import { store } from "../../store";
import { load404Page } from "../../libraries/utils";
import { createSeoOptimizedProductName } from "../../domains2/libraries/Utils";
import routeMapper from "../routeMapper";

// components
const ShopSelection = () => import("../../components/ShopComponents/defaultGlobalTemplate/ShopSelection");
const HomeWrapper = () => import("../../components/ShopComponents/defaultGlobalTemplate/HomeWrapper");
const ProductCatalog = () => import("../../components/ShopComponents/defaultGlobalTemplate/ProductCatalogWrapper");
const ProductDetails = () => import("../../components/ShopComponents/defaultGlobalTemplate/ProductDetailsWrapper");
const ShoppingCart = () => import("../../components/ShopComponents/defaultGlobalTemplate/ShoppingCart/ShoppingCart");
const ShoppingCartAdmin = () => import("../../components/ShopComponents/defaultGlobalTemplate/ShoppingCart/ShoppingCartOld");
const ShopppingCartInformation = () => import("../../components/ShopComponents/defaultGlobalTemplate/ShoppingCart/ShopppingCartInformation");
const ShopList = () => import("../../components/ShopComponents/defaultGlobalTemplate/Shops/ShopList");
const Payment = () => import("../../components/GlobalSharedComponents/Shop/Payment");
const InvoicesWrapper = () => import("../../components/GlobalSharedComponents/Accounting/InvoicesWrapper");
const OrderMessagesWrapper = () => import("../../components/ShopComponents/defaultGlobalTemplate/OrderMessages/OrderMessagesWrapper");
const CookiesWrapper = () => import("../../components/ShopComponents/defaultGlobalTemplate/Cookies/CookiesWrapper");
const CookiesDescription = () => import("../../components/ShopComponents/defaultGlobalTemplate/Cookies/CookiesDescription");
const CookiesConfiguration = () => import("../../components/ShopComponents/defaultGlobalTemplate/Cookies/CookiesConfiguration");
const CatalogPagePreview = () => import("../../components/ShopComponents/defaultGlobalTemplate/CatalogPagePreview");
const Handelsbetingelser = () => import("../../components/ShopComponents/defaultGlobalTemplate/CatalogTemplates/HandelsbetingelserWrapper");

// handlers
import versionCompare from "../../mobile/versionCompare";

export default {
	routes: [
		{
			path: "/",
			name: routeMapper.routeNames.Home,
			component: HomeWrapper,
			beforeEnter: async (to, from, next) => {
				const isObsoleteVersion = await versionCompare(store);

				if (isObsoleteVersion) {
					next("/obsolete-version");
				} else {
					next();
				}
			}
		},
		{
			path: "/handelsbetingelser",
			name: "handelsbetingelser",
			component: Handelsbetingelser
		},
		{
			path: "/category/:categoryId?",
			name: routeMapper.routeNames.productCatalog,
			component: ProductCatalog,
			beforeEnter: async (to, from, next) => {
				const isObsoleteVersion = await versionCompare(store);

				if (to.params.categoryId === undefined) {
					// check for alias categories
					// we only need to check the last element in the url path, i.e. /food/wrong-category -> /wrong-category/
					const tmpPath = (to.path.endsWith("/") ? to.path.slice(0, -1) : to.path); // check for trailing slash
					const categoryName = tmpPath.slice(tmpPath.lastIndexOf("/") + 1);

					const clientRedirect = store.getters.clientRedirects.find(cr => cr.redirectFrom === `/${decodeURIComponent(categoryName)}/`);

					if (clientRedirect) {
						// redirect to the new category
						return next(clientRedirect.redirectTo);
					}

					const category = store.getters.productCatalog.categories.find(c => {
						const aliasPath = c.aliasPath || "";
						return [aliasPath, aliasPath].includes(decodeURIComponent(categoryName));
					});

					if (category) {
						to.params.categoryId = category.extId;
					} else {
						load404Page();
					}
				} else {
					const useOnlyAliasPathForCategories = store.getters.featureToggles.useOnlyAliasPathForCategories;
					const category = store.getters.productCatalog.categories.find(c => `${c.extId}` === to.params.categoryId);

					if (category && typeof category.aliasPath === "string" && category.aliasPath.length > 0) {
						next(`/${encodeURIComponent(category.aliasPath)}/`);
					} else if (useOnlyAliasPathForCategories) {
						load404Page();
					}

					if (category.customRoutes) {
						next(category.customRoutes);
					}
				}

				if (isObsoleteVersion) {
					next("/obsolete-version");
				} else {
					next();
				}
			},
			alias: ["*"]
		},
		{
			path: "/product/:productId",
			name: routeMapper.routeNames.productDetails,
			component: ProductDetails,
			beforeEnter: async (to, from, next) => {
				// in case we need to block wrong urls (not SeoOptimizedProductName)
				const isValidUrl = (() => {
					if (!store.getters.featureToggles.useSeoOptimizedProductNameForRoutes) {
						return true;
					}
					// rule 1: urls must ends with a slash '/'
					const hasTrailingSlash = to.path.endsWith("/");
					if (!(to.query && Object.keys(to.query).length) && !hasTrailingSlash) {
						return false;
					}
					// rule 2: the product in the url must meet the seo conditions
					const originalProductId = to.params.productId;

					const formattedProduct = (() => {
						const productId = parseInt(originalProductId, 10);
						if (Number.isInteger(productId) && productId.toString() === originalProductId.toString()) {
							return originalProductId;
						}
						return createSeoOptimizedProductName(originalProductId);
					})();

					return formattedProduct === originalProductId;
				})();

				const isObsoleteVersion = await versionCompare(store);

				if (isObsoleteVersion) {
					next("/obsolete-version");
				} else {
					if (!isValidUrl) {
						return load404Page();
					}

					const clientRedirect = store.getters.clientRedirects.find(cr => cr.redirectFrom.includes(`/product/${decodeURIComponent(to.params.productId)}/`));
					if (clientRedirect) {
						return next(clientRedirect.redirectTo);
					}
					next();
				}
			}
		},
		{
			path: "/checkout",
			name: routeMapper.routeNames.checkoutCart,
			component: ShoppingCart
		},
		{
			path: "/checkout-admin",
			name: routeMapper.routeNames.checkoutCartAdmin,
			component: ShoppingCartAdmin
		},
		{
			path: "/checkout/information",
			name: routeMapper.routeNames.checkoutCartInformation,
			component: ShopppingCartInformation
		},
		{
			path: "/checkout/step/:stepNumber",
			name: routeMapper.routeNames.checkoutStep,
			component: ShoppingCart
		},
		{
			path: "/shops",
			name: routeMapper.routeNames.shops,
			component: ShopList
		},
		{
			path: "/payment/:action",
			name: routeMapper.routeNames.payment,
			props: {
				"checkoutWithStep": true,
				"increaseStep": false,
				"confirmationStep": "stepFinishWrapper"
			},
			component: Payment
		},
		{
			path: "/invoices/:invoiceId",
			name: routeMapper.routeNames.invoices,
			component: InvoicesWrapper
		},
		{
			path: "/order-messages/:orderId",
			name: routeMapper.routeNames.orderMessages,
			component: OrderMessagesWrapper
		},
		{
			path: "/cookies/",
			component: CookiesWrapper,
			children: [
				{
					path: "",
					name: routeMapper.routeNames.cookiesWrapper,
					component: CookiesDescription
				},
				{
					path: "configuration",
					name: routeMapper.routeNames.cookiesConfiguration,
					component: CookiesConfiguration
				}
			]
		},
		{
			path: "/catalogPagePreview",
			name: routeMapper.routeNames.catalogPagePreview,
			component: CatalogPagePreview
		},
		{
			path: "/shop-selection",
			name: routeMapper.routeNames.shopSelection,
			component: ShopSelection
		}
	]
};
